// CrewDashboard.js — Crew Dashboard shell: header, KPIs, alert shelf, filters, views, sidebar.
import React from 'react';
import './crewDashboard.css';
import { T } from './lib/crewTokens';
import { simNow } from './lib/crewFormat';
import { useCrewData } from './lib/useCrewData';
import { KKpi, CrewGlyph, useTick } from './atoms/CrewAtoms';
import { CardsView } from './views/CardsView';
import { MapView } from './views/MapView';
import { RosterView } from './views/RosterView';
import { FleetView } from './views/FleetView';
import { CrewSidebar } from './CrewSidebar';

const AT = T;

const CFILTERS = [
  { id: 'otrisk', label: 'OT risk', test: (c) => c.ot.level !== 'ontrack' },
  { id: 'behind', label: 'Behind schedule', test: (c) => c.behindMin >= 15 },
  { id: 'vehicle', label: 'Vehicle alerts', test: (c) => c.vehicle.faultCodes.length || c.vehicle.regDays < 0 || c.vehicle.openRepairs.some((r) => r.priority === 'high') || c.vehicle.fuel < 25 || c.vehicle.mtcOverdueMi },
  { id: 'location', label: 'Location flags', test: (c) => c.loc.flags > 0 },
  { id: 'lunch', label: 'On lunch/break', test: (c) => c.members.some((m) => m.status === 'lunch' || m.status === 'break') },
  { id: 'idle', label: 'Idle', test: (c) => c.idleMin >= 35 },
];

function NowClock() {
  useTick(1000);
  const d = new Date(simNow());
  let h = d.getHours(); const m = d.getMinutes(); const s = d.getSeconds();
  const ap = h >= 12 ? 'PM' : 'AM'; h = h % 12 || 12;
  return <span style={{ fontFamily: AT.mono, fontWeight: 700 }}>{h}:{String(m).padStart(2, '0')}:{String(s).padStart(2, '0')} {ap}</span>;
}

function CrewDashboard() {
  const { crews: ALL_CREWS, fleet: FLEET, yard, loading, error } = useCrewData();
  const DIVS = [...new Set(ALL_CREWS.map((c) => c.division))];

  const glyphVal = 'saguaro';
  const friendly = true;

  const [view, setView] = React.useState(() => localStorage.getItem('crew-dash-view') || 'cards');
  const setViewP = (v) => { setView(v); localStorage.setItem('crew-dash-view', v); };
  const [query, setQuery] = React.useState('');
  const [activeFilters, setActiveFilters] = React.useState([]);
  const [divFilter, setDivFilter] = React.useState('all');
  const [selected, setSelected] = React.useState(null);

  const toggleFilter = (id) => setActiveFilters((xs) => xs.includes(id) ? xs.filter((x) => x !== id) : [...xs, id]);

  const rows = React.useMemo(() => {
    let xs = ALL_CREWS;
    if (query.trim()) { const q = query.trim().toLowerCase(); xs = xs.filter((c) => c.name.toLowerCase().includes(q) || c.foreman.toLowerCase().includes(q) || c.vehicle.no.toLowerCase().includes(q) || c.members.some((m) => m.name.toLowerCase().includes(q))); }
    if (divFilter !== 'all') xs = xs.filter((c) => c.division === divFilter);
    for (const f of activeFilters) { const d = CFILTERS.find((x) => x.id === f); xs = xs.filter(d.test); }
    return xs;
  }, [query, activeFilters, divFilter, ALL_CREWS]);

  const shelf = ALL_CREWS.filter((c) => c.status.level === 'critical').slice(0, 4);

  if (!ALL_CREWS.length) {
    const msg = error
      ? (error.message ? `Couldn't load crew data. (${error.message})` : "Couldn't load crew data.")
      : loading
        ? 'Loading crews…'
        : 'No crews scheduled today.';
    return (
      <div className="crew-dash-root" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontSize: 13, fontWeight: 600, color: error ? AT.rust : AT.mute }}>
        <span>{msg}</span>
      </div>
    );
  }

  return (
    <div className="crew-dash-root" style={{ display: 'flex', height: '100vh', overflow: 'hidden', position: 'relative', zIndex: 1 }}>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* header */}
        <div style={{ padding: '15px 20px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: AT.muteSoft, display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><span className="crew-live-dot"></span>Live</span> · <NowClock />
              </div>
              <h1 style={{ fontSize: 19, fontWeight: 800, letterSpacing: '-0.01em', lineHeight: 1.2 }}>Crews &amp; Fleet</h1>
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search crew, member, truck…" style={{ width: 210, padding: '7px 11px', borderRadius: 8, border: `1px solid ${AT.line2}`, background: AT.surface, fontSize: 12, color: AT.ink, outline: 'none' }} />
              <select value={divFilter} onChange={(e) => setDivFilter(e.target.value)} style={{ padding: '7px 9px', borderRadius: 8, border: `1px solid ${AT.line2}`, background: AT.surface, fontSize: 12, color: AT.ink, fontWeight: 600, cursor: 'pointer' }}>
                <option value="all">All divisions</option>
                {DIVS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <div style={{ display: 'flex', background: AT.surface3, border: `1px solid ${AT.line}`, borderRadius: 9, padding: 3, gap: 2 }}>
                {[['cards', 'Crews'], ['map', 'Map'], ['roster', 'Roster'], ['fleet', 'Fleet']].map(([k, l]) => (
                  <button key={k} onClick={() => setViewP(k)} style={{ padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, background: view === k ? AT.surface : 'transparent', color: view === k ? AT.ink : AT.mute, boxShadow: view === k ? '0 1px 2px rgba(28,36,25,0.1)' : 'none' }}>{l}</button>
                ))}
              </div>
            </div>
          </div>

          {/* KPI strip */}
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <KKpi label="Crews active" value={FLEET.crews} sub={`${FLEET.driving} driving · ${FLEET.crews - FLEET.driving} on site`} />
            <KKpi label="On the clock" value={FLEET.membersOn} sub={`${FLEET.onLunch} lunch · ${FLEET.onBreak} break`} />
            <KKpi label="Tickets today" value={`${FLEET.ticketsDone}/${FLEET.ticketsSched}`} sub={`${Math.round(FLEET.ticketsDone / FLEET.ticketsSched * 100)}% complete`} />
            <KKpi label="Behind schedule" value={FLEET.behind} tone={FLEET.behind > 0 ? 'warn' : 'good'} sub="15+ min behind plan" />
            <KKpi label="OT risk" value={FLEET.otRisk} tone={FLEET.otWill > 0 ? 'bad' : FLEET.otRisk > 0 ? 'warn' : 'good'} sub={`${FLEET.otWill} will exceed 8h`} />
            <KKpi label="Vehicle alerts" value={FLEET.vehicleAlerts} tone={FLEET.vehicleAlerts > 0 ? 'bad' : 'good'} sub="faults · reg · repairs" />
            <KKpi label="Location flags" value={FLEET.locationFlags} tone={FLEET.locationFlags > 0 ? 'warn' : 'good'} sub="off-site · phone vs truck" />
            <KKpi label="Idle today" value={`${FLEET.idleHrs}h`} tone={FLEET.idleHrs > 4 ? 'bad' : FLEET.idleHrs > 2 ? 'warn' : 'good'} sub={`vs ${FLEET.onsiteHrs}h on-site`} />
          </div>

          {/* alert shelf */}
          {shelf.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: AT.rust, marginBottom: 6 }}>
                {friendly ? `${shelf.length} crews need a closer look` : 'Needs attention'}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(4, shelf.length)}, 1fr)`, gap: 8 }}>
                {shelf.map((c) => (
                  <div key={c.id} onClick={() => setSelected(c)} className="crew-shelf-card" style={{ background: AT.surface, border: '1px solid #ecc8b8', borderRadius: 10, padding: '9px 11px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <CrewGlyph level="critical" variant={glyphVal} size={16} />
                      <span style={{ fontSize: 11.5, fontWeight: 800, color: AT.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                      <span style={{ marginLeft: 'auto', fontFamily: AT.mono, fontSize: 9.5, fontWeight: 700, color: AT.mute, flexShrink: 0 }}>{c.vehicle.no}</span>
                    </div>
                    <div style={{ fontSize: 10.5, color: AT.ink2, lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{c.status.reasons[0]}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* filter chips */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12, marginBottom: 10, flexWrap: 'wrap' }}>
            {CFILTERS.map((f) => {
              const on = activeFilters.includes(f.id); const n = ALL_CREWS.filter(f.test).length;
              return (
                <button key={f.id} onClick={() => toggleFilter(f.id)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 99, border: `1px solid ${on ? AT.green : AT.line2}`, cursor: 'pointer', fontSize: 11.5, fontWeight: 700, background: on ? AT.green : AT.surface, color: on ? '#fbf8f1' : AT.ink2, transition: 'all 0.13s' }}>
                  {f.label}<span style={{ fontFamily: AT.mono, fontSize: 9.5, fontWeight: 700, opacity: on ? 0.85 : 0.6 }}>{n}</span>
                </button>
              );
            })}
            {(activeFilters.length > 0 || divFilter !== 'all' || query) && <button onClick={() => { setActiveFilters([]); setDivFilter('all'); setQuery(''); }} style={{ border: 'none', background: 'transparent', color: AT.mute, fontSize: 11, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2 }}>Clear</button>}
            <span style={{ marginLeft: 'auto', fontSize: 11, color: AT.muteSoft, fontWeight: 600 }}>{rows.length} of {ALL_CREWS.length} crews</span>
          </div>
        </div>

        {/* view area */}
        <div className="crew-page-enter" key={view} style={{ flex: 1, minHeight: 0, overflow: (view === 'cards') ? 'auto' : 'hidden', padding: '0 20px 14px' }}>
          {view === 'cards' && <CardsView rows={rows} selected={selected} onSelect={setSelected} glyph={glyphVal} />}
          {view === 'map' && <MapView rows={rows} allCrews={ALL_CREWS} yard={yard} selected={selected} onSelect={setSelected} glyph={glyphVal} />}
          {view === 'roster' && <RosterView rows={rows} selected={selected} onSelect={setSelected} glyph={glyphVal} />}
          {view === 'fleet' && <FleetView rows={rows} selected={selected} onSelect={setSelected} glyph={glyphVal} />}
        </div>

        {/* footer */}
        <div style={{ flexShrink: 0, padding: '7px 20px 9px', borderTop: `1px solid ${AT.line}`, background: AT.surface, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, color: AT.mute }}>
            {friendly ? `${FLEET.healthy} crews are humming · ${FLEET.critical} need a closer look · ${FLEET.ticketsDone} of ${FLEET.ticketsSched} tickets done` : `${FLEET.healthy} healthy · ${FLEET.watch} watch · ${FLEET.critical} critical`}
          </span>
          <span style={{ marginLeft: 'auto', fontSize: 10, color: AT.muteSoft, fontFamily: AT.mono }}>GPS + CRM · live</span>
        </div>
      </div>

      {selected && <CrewSidebar c={selected} onClose={() => setSelected(null)} glyph={glyphVal} />}
    </div>
  );
}

export default CrewDashboard;
